import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TemplateModal = ({ isOpen, onClose, template = null, onSave }) => {
  const [formData, setFormData] = useState({
    templateId: '',
    name: '',
    templateMessage: '',
    isStaticMessage: false,
    isRuntimeMessage: false,
    messageType: 'WHATSAPP',
    triggerApplicableType: 'MANUAL',
    templateType: 'NOTIFICATION',
    provider: '',
    language: 'en',
    category: '',
    isActive: true,
    templatedVars: []
  });
  const [variables, setVariables] = useState('');
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        templateId: template.template_id || '',
        name: template.name || '',
        templateMessage: template.template_message || '',
        isStaticMessage: template.is_static_message || false,
        isRuntimeMessage: template.is_runtime_message || false,
        messageType: template.message_type || 'WHATSAPP',
        triggerApplicableType: template.trigger_applicable_type || 'MANUAL',
        templateType: template.template_type || 'NOTIFICATION',
        provider: template.provider || '',
        language: template.language || 'en',
        category: template.category || '',
        isActive: template.is_active !== false,
        templatedVars: template.templated_vars || []
      });
      setVariables((template.templated_vars || []).join(', '));
    } else {
      // Reset form for new template
      setFormData({
        templateId: '',
        name: '',
        templateMessage: '',
        isStaticMessage: false,
        isRuntimeMessage: false,
        messageType: 'WHATSAPP',
        triggerApplicableType: 'MANUAL',
        templateType: 'NOTIFICATION',
        provider: '',
        language: 'en',
        category: '',
        isActive: true,
        templatedVars: []
      });
      setVariables('');
    }
    setErrors({});
  }, [template, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleVariablesChange = (value) => {
    setVariables(value);
    const vars = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
    setFormData(prev => ({ ...prev, templatedVars: vars }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.templateId.trim()) {
      newErrors.templateId = 'Template ID is required';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }
    // Runtime messages don't need templateMessage (it's provided at runtime)
    if (!formData.isRuntimeMessage && !formData.templateMessage.trim()) {
      newErrors.templateMessage = 'Template message is required';
    }
    // Only validate variables if not static and not runtime
    if (!formData.isStaticMessage && !formData.isRuntimeMessage) {
      // Check if variables match placeholders in message
      const placeholders = formData.templateMessage.match(/\{\{(\w+)\}\}/g) || [];
      const placeholderVars = placeholders.map(p => p.replace(/\{\{|\}\}/g, ''));
      const definedVars = formData.templatedVars;
      
      const missingVars = placeholderVars.filter(v => !definedVars.includes(v));
      if (missingVars.length > 0) {
        newErrors.templateMessage = `Missing variables in template: ${missingVars.join(', ')}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      const templateData = {
        templateId: formData.templateId,
        name: formData.name,
        templateMessage: formData.isRuntimeMessage ? null : formData.templateMessage,
        isStaticMessage: formData.isStaticMessage,
        isRuntimeMessage: formData.isRuntimeMessage,
        messageType: formData.messageType,
        triggerApplicableType: formData.triggerApplicableType,
        templateType: formData.templateType,
        provider: formData.provider || null,
        language: formData.language,
        category: formData.category || null,
        isActive: formData.isActive,
        templatedVars: (formData.isStaticMessage || formData.isRuntimeMessage) ? [] : formData.templatedVars,
        approvalStatus: template?.approval_status || 'PENDING'
      };

      await onSave(templateData, template?.id);
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      setErrors({ submit: error.message || 'Failed to save template' });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {template ? 'Edit Template' : 'Create New Template'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm text-red-700">{errors.submit}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Template ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.templateId}
                onChange={(e) => handleChange('templateId', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.templateId ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., welcome_message_v1"
              />
              {errors.templateId && (
                <p className="text-xs text-red-600 mt-1">{errors.templateId}</p>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Welcome Message"
              />
              {errors.name && (
                <p className="text-xs text-red-600 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Message Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.messageType}
                onChange={(e) => handleChange('messageType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="SMS">SMS</option>
                <option value="WHATSAPP">WhatsApp</option>
                <option value="EMAIL">Email</option>
              </select>
            </div>

            {/* Template Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.templateType}
                onChange={(e) => handleChange('templateType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="OTP">OTP</option>
                <option value="NOTIFICATION">Notification</option>
                <option value="REMINDER">Reminder</option>
                <option value="ALERT">Alert</option>
                <option value="MARKETING">Marketing</option>
                <option value="TRANSACTIONAL">Transactional</option>
                <option value="PROMOTIONAL">Promotional</option>
              </select>
            </div>

            {/* Trigger Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trigger Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.triggerApplicableType}
                onChange={(e) => handleChange('triggerApplicableType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="MANUAL">Manual Only</option>
                <option value="AUTOMATIC">Automatic Only</option>
                <option value="BOTH">Both</option>
              </select>
            </div>

            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider
              </label>
              <input
                type="text"
                value={formData.provider}
                onChange={(e) => handleChange('provider', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., TWILIO, WHATSAPP_BUSINESS"
              />
            </div>
          </div>

          {/* Runtime Message Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isRuntime"
              checked={formData.isRuntimeMessage}
              onChange={(e) => {
                const isRuntime = e.target.checked;
                handleChange('isRuntimeMessage', isRuntime);
                if (isRuntime) {
                  // When runtime is enabled, disable static and clear message
                  handleChange('isStaticMessage', false);
                  handleChange('templateMessage', '');
                  setVariables('');
                }
              }}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="isRuntime" className="text-sm font-medium text-gray-700">
              Runtime Message (user types message at send time)
            </label>
          </div>

          {/* Static Message Toggle (only if not runtime) */}
          {!formData.isRuntimeMessage && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isStatic"
                checked={formData.isStaticMessage}
                onChange={(e) => handleChange('isStaticMessage', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isStatic" className="text-sm font-medium text-gray-700">
                Static Message (no variables)
              </label>
            </div>
          )}

          {/* Variables (only if not static and not runtime) */}
          {!formData.isStaticMessage && !formData.isRuntimeMessage && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Variables (comma-separated)
              </label>
              <input
                type="text"
                value={variables}
                onChange={(e) => handleVariablesChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., name, otp, date"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use these variables in your message as: {'{{name}}'}, {'{{otp}}'}, etc.
              </p>
            </div>
          )}

          {/* Template Message (only if not runtime) */}
          {!formData.isRuntimeMessage && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.templateMessage}
                onChange={(e) => handleChange('templateMessage', e.target.value)}
                rows={6}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  errors.templateMessage ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder={formData.isStaticMessage 
                  ? "Enter your message here..." 
                  : "Enter your message with variables like: Hello {{name}}, your OTP is {{otp}}."
                }
              />
              {errors.templateMessage && (
                <p className="text-xs text-red-600 mt-1">{errors.templateMessage}</p>
              )}
              {!formData.isStaticMessage && formData.templatedVars.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Available variables: {formData.templatedVars.map(v => `{{${v}}}`).join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Runtime Message Info */}
          {formData.isRuntimeMessage && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Runtime Message Template:</strong> This template will allow users to type the message content at send time. 
                The template ID will be used for provider compliance, but the actual message will be provided by the user.
              </p>
            </div>
          )}

          {/* Active Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
              Active
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={isSaving}
            iconName="Save"
          >
            {isSaving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;

